import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { getCourseById, getCourseLessonProgress } from '../../../api';
import { getUserCourseSectionPaths } from '../../../utils/userCoursePaths';
import Certificate from '../../../components/Certificate';
import PageLoader from '../../../components/PageLoader';



const CourseQuiz = () => {
  const { courseId } = useParams();
  const location = useLocation();
  const { courseBase, listBase, listLabel } = getUserCourseSectionPaths(location.pathname, courseId);
  const { user, getCourses, getEnrolledCourseIds, enrollInCourse } = useAuth();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState([]);
  const [score, setScore] = useState(0);
  const [showResult, setShowResult] = useState(false);
  const [justEnrolled, setJustEnrolled] = useState(false);
  const [completionLoading, setCompletionLoading] = useState(false);
  const [allLessonsCompleted, setAllLessonsCompleted] = useState(false);

  const enrolledIds = user?.id ? getEnrolledCourseIds(user.id) : [];
  const isEnrolled = enrolledIds.some((id) => String(id) === String(courseId));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const list = getCourses() || [];
      const local = list.find((x) => String(x.id) === String(courseId));
      if (local) {
        if (!cancelled) {
          setCourse(local);
          setLoading(false);
        }
        return;
      }
      try {
        const apiCourse = await getCourseById(courseId);
        if (!cancelled) setCourse(apiCourse || null);
      } catch {
        if (!cancelled) setCourse(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [courseId, getCourses]);

  const handleEnrol = () => {
    if (!user?.id || !courseId) return;
    enrollInCourse(user.id, courseId);
    setJustEnrolled(true);
  };

  const orderedQuestions = useMemo(() => {
    const raw = course?.quiz && Array.isArray(course.quiz) ? course.quiz : [];
    if (raw.length <= 1) return raw;
    const oddPositions = [];
    const evenPositions = [];
    raw.forEach((q, idx) => {
      if (idx % 2 === 0) oddPositions.push(q); // Q1, Q3, Q5...
      else evenPositions.push(q); // Q2, Q4, Q6...
    });
    return [...oddPositions, ...evenPositions];
  }, [course?.quiz]);

  useEffect(() => {
    setSelectedAnswers(Array.from({ length: orderedQuestions.length }, () => null));
    setCurrentQuestion(0);
    setScore(0);
    setShowResult(false);
  }, [orderedQuestions.length, courseId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user?.id || !course?.id || !isEnrolled) {
        if (!cancelled) setAllLessonsCompleted(false);
        return;
      }
      setCompletionLoading(true);
      try {
        const data = await getCourseLessonProgress(course.id);
        if (cancelled) return;
        const statuses = Array.isArray(data?.lessonStatuses) ? data.lessonStatuses : [];
        setAllLessonsCompleted(statuses.length > 0 && statuses.every((row) => row.status === 'completed'));
      } catch {
        if (!cancelled) setAllLessonsCompleted(false);
      } finally {
        if (!cancelled) setCompletionLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id, course?.id, isEnrolled]);

  const handleAnswerSelect = (selectedIndex) => {
    setSelectedAnswers((prev) => {
      const next = [...prev];
      next[currentQuestion] = selectedIndex;
      return next;
    });
  };

  const handleNext = () => {
    if (currentQuestion < orderedQuestions.length - 1) {
      setCurrentQuestion((q) => q + 1);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion((q) => q - 1);
    }
  };

  const handleSubmitQuiz = () => {
    if (orderedQuestions.length === 0) return;
    const total = orderedQuestions.reduce((acc, q, idx) => {
      const correctIndex = typeof q.correct === 'number' ? q.correct : 0;
      return selectedAnswers[idx] === correctIndex ? acc + 1 : acc;
    }, 0);
    setScore(total);
    setShowResult(true);
  };

  const resetQuiz = () => {
    setCurrentQuestion(0);
    setSelectedAnswers(Array.from({ length: orderedQuestions.length }, () => null));
    setScore(0);
    setShowResult(false);
  };

  const handlePrintCertificate = () => {
    window.print();
  };


  if (loading) {
    return <PageLoader fullScreen={false} />;
  }

  if (!course) {
    return (
      <div className="max-w-lg mx-auto">
        <div className="mb-6">
          <Link to={listBase} className="text-gray-600 hover:text-primary-600 text-sm font-medium">
            ← {listLabel}
          </Link>
        </div>
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
          <p className="text-gray-600">Course not found.</p>
          <Link to={listBase} className="mt-4 inline-block text-primary-600 hover:underline">
            Back
          </Link>
        </div>
      </div>
    );
  }

  if (!isEnrolled && !justEnrolled) {
    return (
      <div className="max-w-lg mx-auto">
        <div className="mb-6">
          <Link to={listBase} className="text-gray-600 hover:text-primary-600 text-sm font-medium">
            ← {listLabel}
          </Link>
        </div>
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 text-center">
          <div className="w-16 h-16 rounded-2xl bg-primary-100 flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl">📋</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Course Quiz: {course.title}</h1>
          <p className="text-gray-600 mb-6">
            This quiz is part of the course. You need to enrol in the course to access it.
          </p>
          <p className="text-gray-700 font-medium mb-6">
            Would you like to enrol in this course to take the quiz?
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <button
              type="button"
              onClick={handleEnrol}
              className="px-6 py-3 rounded-xl font-semibold bg-primary-600 text-white hover:bg-primary-700 transition-colors"
            >
              Yes, enrol and take quiz
            </button>
            <Link
              to={listBase}
              className="px-6 py-3 rounded-xl font-semibold bg-gray-200 text-gray-800 hover:bg-gray-300 transition-colors"
            >
              No, go back
            </Link>
          </div>
          <p className="text-sm text-gray-500 mt-6">
            You can also browse all courses from <Link to="/user/browse" className="text-primary-600 hover:underline">Courses</Link>.
          </p>
        </div>
      </div>
    );
  }

  if (orderedQuestions.length === 0) {
    return (
      <div className="max-w-lg mx-auto">
        <div className="mb-6">
          <Link to={courseBase} className="text-gray-600 hover:text-primary-600 text-sm font-medium">
            ← Back to course
          </Link>
        </div>
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
          <p className="text-gray-600">No quiz available for this course yet.</p>
          <Link to={courseBase} className="mt-4 inline-block text-primary-600 hover:underline">
            Back to course
          </Link>
        </div>
      </div>
    );
  }

  if (completionLoading) {
    return (
      <div className="max-w-lg mx-auto">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
          <p className="text-gray-600">Checking lesson completion...</p>
        </div>
      </div>
    );
  }

  if (!allLessonsCompleted) {
    return (
      <div className="max-w-lg mx-auto">
        <div className="mb-6">
          <Link to={courseBase} className="text-gray-600 hover:text-primary-600 text-sm font-medium">
            ← Back to course
          </Link>
        </div>
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
          <p className="text-gray-700 font-medium">Complete all lessons first to unlock this quiz.</p>
          <Link to={courseBase} className="mt-4 inline-block text-primary-600 hover:underline">
            Go to lessons
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between flex-wrap gap-4">
        <Link to={courseBase} className="text-gray-600 hover:text-primary-600 text-sm font-medium">
          ← Back to course
        </Link>
        <h1 className="text-2xl font-bold text-gray-800">{course.title} – Quiz</h1>
      </div>

      {!showResult ? (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-600">
                Question {currentQuestion + 1} of {orderedQuestions.length}
              </span>
              <span className="text-sm font-medium text-primary-600">
                Answered: {selectedAnswers.filter((x) => x !== null).length}/{orderedQuestions.length}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${((currentQuestion + 1) / orderedQuestions.length) * 100}%` }}
              />
            </div>
          </div>

          <h2 className="text-xl font-bold text-gray-900 mb-6">
            {orderedQuestions[currentQuestion].question}
          </h2>

          <div className="space-y-3">
            {(orderedQuestions[currentQuestion].options || []).map((option, index) => (
              <button
                key={index}
                type="button"
                onClick={() => handleAnswerSelect(index)}
                className={`w-full text-left p-4 border-2 rounded-xl transition-all duration-200 font-medium ${
                  selectedAnswers[currentQuestion] === index
                    ? 'bg-primary-50 border-primary-400 text-primary-900'
                    : 'bg-gray-50 hover:bg-primary-50 border-gray-200 hover:border-primary-300 text-gray-800'
                }`}
              >
                {option}
              </button>
            ))}
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
            <button
              type="button"
              onClick={handleBack}
              disabled={currentQuestion === 0}
              className="px-5 py-2.5 rounded-xl font-semibold bg-gray-200 text-gray-800 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Back
            </button>
            {currentQuestion < orderedQuestions.length - 1 ? (
              <button
                type="button"
                onClick={handleNext}
                className="px-5 py-2.5 rounded-xl font-semibold bg-primary-600 text-white hover:bg-primary-700 transition-colors"
              >
                Next
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmitQuiz}
                disabled={selectedAnswers.some((x) => x === null)}
                className="px-5 py-2.5 rounded-xl font-semibold bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Submit quiz
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
          <div className="w-24 h-24 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-12 h-12 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Quiz completed</h2>
          <p className="text-lg text-gray-600 mb-4">
            Your score: <span className="text-primary-600 font-bold">{score}</span> out of {orderedQuestions.length}
          </p>
          <p className="text-gray-500 mb-6">
            {score === orderedQuestions.length
              ? 'Perfect! 🎉'
              : score >= orderedQuestions.length / 2
              ? 'Good job! 👍'
              : 'Keep learning! 💪'}
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {score >= orderedQuestions.length / 2 && (
              <button
                type="button"
                onClick={handlePrintCertificate}
                className="px-6 py-3 rounded-xl font-semibold bg-emerald-600 text-white hover:bg-emerald-700 transition-all shadow-lg shadow-emerald-200 flex items-center gap-2"
              >
                <span>🎓</span>
                Download Certificate
              </button>
            )}
            <button
              type="button"
              onClick={resetQuiz}
              className="px-6 py-3 rounded-xl font-semibold bg-primary-600 text-white hover:bg-primary-700 transition-colors"
            >
              Take quiz again
            </button>
            <Link
              to={courseBase}
              className="px-6 py-3 rounded-xl font-semibold bg-gray-200 text-gray-800 hover:bg-gray-300 transition-colors"
            >
              Back to course
            </Link>
          </div>
          
          {/* Hidden Certificate for Printing */}
          <div className="hidden print:block fixed inset-0 bg-white z-[9999]">
            <div className="flex items-center justify-center min-h-screen">
              <Certificate 
                userName={`${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.email}
                courseTitle={course?.title}
                date={new Date().toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' })}
              />
            </div>
          </div>
          
          <style dangerouslySetInnerHTML={{ __html: `
            @media print {
              body * {
                visibility: hidden;
              }
              .print\\:block, .print\\:block * {
                visibility: visible;
              }
              .print\\:block {
                position: absolute;
                left: 0;
                top: 0;
                width: 100%;
                height: 100%;
              }
              @page {
                size: landscape;
                margin: 0;
              }
            }
          `}} />
        </div>
      )}
    </div>

  );
};

export default CourseQuiz;
